import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Landmark,
  Cpu,
  MapPin,
  Download,
} from "lucide-react";
import ContactCTA from "./ContactCTA";

// SMRU student images (Hero1/Hero2) + Unsplash campus shots, each with a caption.
const HERO_SLIDES = [
  {
    src: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1600&auto=format&fit=crop",
    alt: "Students collaborating on a project",
    tag: "Real Projects from Day 1",
    headline: "Build together. Ship together.",
  },
  {
    src: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?q=80&w=1600&auto=format&fit=crop",
    alt: "Student carrying textbooks on campus",
    tag: "Guaranteed Internship",
    headline: "Real-world industry experience.",
  },
  {
    src: "/partners/smru/Hero1.jpeg",
    alt: "Students studying as a group",
    tag: "Industry-Powered Training",
    headline: "Skills the world actually hires for.",
  },
  {
    src: "/partners/smru/Hero2.jpeg",
    alt: "SMRU students working together",
    tag: "Mentored by the Best",
    headline: "Engineers from top global tech.",
  },
];

const FALLBACK_IMG = "/partners/smru/SMRU_COLLEGE.jpeg";

export default function SmruHero() {
  const [slide, setSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const count = HERO_SLIDES.length;

  // Auto-advance every 4.5s (paused on hover) — same cadence as the main site.
  useEffect(() => {
    if (isPaused) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % count), 4500);
    return () => clearInterval(t);
  }, [isPaused, count]);

  const trust = [
    {
      Icon: Landmark,
      line1: "UGC 2(f) Recognized",
      line2: "St. Mary's University",
    },
    {
      Icon: Cpu,
      line1: "AI.Prof Ecosystem",
      line2: "Industry-ready AI Infrastructure from Day 1",
    },
    {
      Icon: MapPin,
      line1: "Near Ramoji Film City",
      line2: "Deshmukhi, Hyderabad",
    },
  ];

  return (
    <section
      id="top"
      className="relative pb-20 overflow-hidden pt-6 sm:pt-8"
    >
      {/* Soft brand blob — same treatment as the main website hero. */}
      <div className="absolute top-10 -right-32 w-[600px] h-[600px] rounded-full blur-3xl opacity-50 bg-gradient-to-tr from-aiorange to-aipeach float-fast" />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* LEFT — headline + CTAs */}
        <div className="lg:col-span-6 text-center lg:text-left">
          {/* AI.Prof logo — directly above the headline */}
          <motion.img
            src="/ai_prof.png"
            alt="AI.Prof"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="h-12 sm:h-16 w-auto object-contain mb-5 mx-auto lg:mx-0"
          />

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-black tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[1.1]"
          >
            <span className="text-aiblue">India's First</span>
            <br />
            <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
              AI-Powered B.Tech
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-4 font-display font-black text-aiorange text-xl sm:text-3xl lg:text-[2.5rem] leading-tight"
          >
            at St. Mary's University (SMRU), Hyderabad
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-6 text-base sm:text-lg font-bold text-gray-600 max-w-xl mx-auto lg:mx-0"
          >
            A 4-year career ecosystem combining SMRU's academic legacy with
            AI.Prof's industry-powered training built for students going
            global.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="mt-8 flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-3"
          >
            <ContactCTA className="cta-pulse items-center justify-center gap-2 bg-aiblue hover:bg-blue-700 text-white rounded-full px-7 py-3.5 font-semibold transition-colors" />
            <Link
              to="/RFC/brochure" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-gray-200 text-gray-800 rounded-full px-7 py-3.5 font-semibold hover:border-aiblue hover:text-aiblue transition-colors"
            >
              <Download className="w-5 h-5" />
              Download Brochure
            </Link>
          </motion.div>
        </div>

        {/* RIGHT — rotating student image card (main-website style). */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:col-span-6 relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => {
            setIsPaused(false);
            setSlide((s) => (s + 1) % count);
          }}
        >
          <div className="relative h-[320px] sm:h-[440px] lg:h-[520px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide}
                initial={{ opacity: 0, scale: 0.9, rotate: -4 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.9, rotate: 4 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-black/5 bg-gray-100"
              >
                <img
                  src={HERO_SLIDES[slide].src}
                  alt={HERO_SLIDES[slide].alt}
                  onError={(e) => {
                    if (e.currentTarget.src !== window.location.origin + FALLBACK_IMG)
                      e.currentTarget.src = FALLBACK_IMG;
                  }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                {/* Caption scrim + text */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white text-left">
                  <span className="text-xs uppercase tracking-widest font-bold text-amber-300">
                    {HERO_SLIDES[slide].tag}
                  </span>
                  <h3 className="mt-1.5 font-display text-2xl sm:text-3xl font-bold leading-tight drop-shadow">
                    {HERO_SLIDES[slide].headline}
                  </h3>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Pagination dots — below the card, like the main site */}
            <div className="absolute -bottom-8 left-0 right-0 z-10 flex justify-center gap-2">
              {HERO_SLIDES.map((s, i) => (
                <button
                  key={s.src}
                  onClick={() => setSlide(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${
                    i === slide ? "w-8 bg-aiblue" : "w-2 bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Trust strip — left-aligned, items kept close together. */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.6 }}
        className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10 mt-12 lg:mt-16 flex flex-col sm:flex-row sm:flex-wrap items-center sm:items-start gap-5 sm:gap-x-10 lg:gap-x-12"
      >
        {trust.map(({ Icon, line1, line2 }) => (
          <div key={line1} className="flex items-center gap-3">
            <span className="flex-shrink-0 h-10 w-10 rounded-full bg-aiblue/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-aiblue" />
            </span>
            <span className="text-sm leading-tight">
              <span className="block font-semibold text-gray-900">{line1}</span>
              <span className="block text-gray-500">{line2}</span>
            </span>
          </div>
        ))}
      </motion.div>
    </section>
  );
}
